<template>
  <div v-if="form == null" style="padding: 20px;">
    Chargement...
  </div>

  <div v-else style="padding: 20px;">
<p class="field-help">Modifiez vos préférences puis validez-les pour recalculer cette semaine. « Enregistrer sans recalculer » conserve seulement vos réglages.</p><div class="pref-save-actions"><span v-if="saveFeedback" role="status">{{ saveFeedback }}</span><span v-if="saveError" class="pref-save-error" role="alert">{{ saveError }}</span><span v-if="deepSearchAvailable" class="deep-search-note">Le calcul rapide a atteint sa limite de temps.</span><button type="button" class="button" :disabled="savingAll || regenerating || restoringPrevious" @click="saveAllPreferences">{{ savingAll ? "Enregistrement…" : "Enregistrer sans recalculer" }}</button><button type="button" class="button" :disabled="savingAll || regenerating || restoringPrevious" @click="applyPreferencesToMenus(false)">{{ regenerating ? "Mise à jour des menus…" : "Valider mes préférences et mettre à jour ma semaine" }}</button><button v-if="canRestorePreferences" type="button" class="button button-secondary" :disabled="savingAll || regenerating || restoringPrevious" @click="restorePreviousPreferences">{{ restoringPrevious ? "Restauration…" : "Revenir à mes anciennes préférences" }}</button><button v-if="deepSearchAvailable" type="button" class="button deep-search-button" :disabled="regenerating" @click="applyPreferencesToMenus(true)">{{ deepSearchRunning ? "Recherche approfondie…" : "Lancer une recherche approfondie (jusqu’à 2 min)" }}</button></div>
<div v-if="showLeaveDialog" class="leave-overlay"><div class="leave-dialog" role="dialog" aria-modal="true" aria-labelledby="leave-title"><h2 id="leave-title">Enregistrer avant de quitter ?</h2><p>Vos derniers choix ne sont pas sauvegardés. Enregistrer conserve vos préférences sans recalculer les menus.</p><p v-if="saveError" role="alert">{{ saveError }}</p><button class="button" :disabled="savingAll" @click="resolveLeave(true)">Enregistrer et quitter</button><button @click="resolveLeave(false)">Quitter sans enregistrer</button><button :disabled="savingAll" @click="cancelLeave">Rester sur cette page</button></div></div>
<div v-if="showMealPrepDisableConfirm" class="leave-overlay"><div class="leave-dialog" role="alertdialog" aria-modal="true" aria-labelledby="meal-prep-disable-title"><h2 id="meal-prep-disable-title">Retirer l’organisation en cuisine ?</h2><p>Le bouton d’accès au plan sera retiré de votre semaine. Vos menus ne seront pas modifiés.</p><p v-if="saveError" role="alert">{{ saveError }}</p><button type="button" class="button" :disabled="savingMealPrepOption" @click="confirmMealPrepDisable">{{ savingMealPrepOption ? "Enregistrement…" : "Oui, retirer l’option" }}</button><button type="button" :disabled="savingMealPrepOption" @click="showMealPrepDisableConfirm = false">Garder l’option</button></div></div>
    <!-- 1. GRANDES PRÉFÉRENCES -->
    <section class="pref-section">
      <div class="section-heading">
        <span class="section-number">1</span>
        <div><h2>Mes préférences pour mes menus de la semaine</h2><p>Tous les réglages qui servent à construire ma semaine sont regroupés ici.</p></div>
      </div>
      <div class="card main-pref-card">
        <div class="pref-grid">
          <div class="pref-field">
            <label class="label-with-help">
              Mon alimentation
              <span
                class="pref-help"
                tabindex="0"
                data-help="Végétarien et sans gluten sont des contraintes communes aux menus de la semaine et aux repas personnalisés. « IG bas » concerne uniquement la construction de la semaine."
              >?</span>
            </label>
            <select class="form-input diet-select" v-model="form.option">
              <option value="none">Variée – tous les plats MMM</option>
              <option value="vegetarian">Végétarienne</option>
              <option value="gluten-free">Sans gluten</option>
              <option value="low-glycemic">À indice glycémique bas</option>
            </select>
          </div>

          <div class="pref-field">
            <label>Privilégier les aliments bon marché</label>
            <div class="binary-options">
              <label :class="{ selected: form.prefer_cheap_meals === true }"><input type="radio" v-model="form.prefer_cheap_meals" :value="true"> Oui</label>
              <label :class="{ selected: form.prefer_cheap_meals === false }"><input type="radio" v-model="form.prefer_cheap_meals" :value="false"> Non</label>
            </div>
          </div>
        </div>

        <div class="protein-pref-row">
          <label class="label-with-help">
            Protéines dans mes menus de la semaine
            <span
              class="pref-help"
              tabindex="0"
              data-help="MMM adapte déjà automatiquement les protéines à votre profil. L’option Maximum raisonnable permet d’aller jusqu’à 130 % de cet objectif en augmentant uniquement les sources protéiques. Elle peut notamment être utile aux sportifs, aux personnes âgées ou à ceux qui souhaitent renforcer ponctuellement leur apport protéique."
            >?</span>
          </label>
          <div class="binary-options protein-options">
            <label :class="{ selected: form.protein_mode === 'standard' }">
              <input type="radio" v-model="form.protein_mode" value="standard"> Standard
            </label>
            <label :class="{ selected: form.protein_mode === 'max' }">
              <input type="radio" v-model="form.protein_mode" value="max"> Maximum raisonnable
            </label>
          </div>
        </div>

        <div class="pantry-preference">
          <div>
            <strong>J’ai déjà des aliments à utiliser ?</strong>
            <p>Je peux les intégrer à ma semaine en gardant l’équilibre de mes menus.</p>
          </div>
          <button type="button" class="button" @click="$emit('pantry-request')">Ajouter mes aliments</button>
        </div>

        <div class="starter-divider"></div>
        <div class="pref-field meal-prep-grouping-pref">
          <label>Préparer plusieurs repas à l’avance</label>
          <div class="starter-options meal-prep-options">
            <label class="starter-option" :class="{ selected: !form.meal_prep_grouping_enabled }">
              <input type="radio" name="meal-prep-grouping-enabled" v-model="form.meal_prep_grouping_enabled" :value="false" @change="onMealPrepOptionChange(false)" />
              <span>Non, je ne souhaite pas préparer plusieurs repas à l’avance</span>
            </label>
            <label class="starter-option" :class="{ selected: form.meal_prep_grouping_enabled }">
              <input type="radio" name="meal-prep-grouping-enabled" v-model="form.meal_prep_grouping_enabled" :value="true" @change="onMealPrepOptionChange(true)" />
              <span>Oui, je souhaite organiser la préparation de plusieurs repas</span>
            </label>
          </div>
          <div v-if="form.meal_prep_grouping_enabled" class="meal-prep-grouping-controls">
            <p class="field-help">La semaine reste identique. MMM regroupe les préparations utiles à partir des repas que vous choisissez.</p>
            <div class="meal-prep-group-size-row">
              <label for="meal-prep-group-size">Combien de repas voulez-vous préparer ensemble ?</label>
              <select id="meal-prep-group-size" class="form-input" v-model.number="form.meal_prep_group_size">
                <option :value="2">2 repas</option>
                <option :value="3">3 repas</option>
                <option :value="4">4 repas</option>
                <option :value="5">5 repas</option>
              </select>
            </div>
            <button type="button" class="button meal-prep-open-button" :disabled="openingMealPrep || savingAll || regenerating" @click="openMealPrepFromPreferences">
              {{ openingMealPrep ? "Enregistrement des préférences…" : "Choisir les repas à préparer" }}
            </button>
          </div>
        </div>

        <div class="starter-divider"></div>
        <div class="pref-field"><label>Entrées dans mes menus</label><p class="field-help">Par défaut, MMM propose une entrée au déjeuner uniquement. Vous pouvez modifier ce choix. Les entrées simples et rapides sont privilégiées ; des entrées plus consistantes sont favorisées lorsque les besoins énergétiques du foyer sont élevés.</p>
          <div class="starter-options"><label v-for="choice in starterChoices" :key="choice.value" class="starter-option" :class="{ selected: form.starter_preference === choice.value }"><input type="radio" v-model="form.starter_preference" :value="choice.value" /><span>{{ choice.label }}</span></label></div>
        </div>
        <div v-if="form.starter_preference === 'custom'" class="starter-custom"><div class="starter-custom-title">Je choisis les repas concernés</div><div class="starter-week"><div v-for="day in starterDays" :key="day.key" class="starter-day"><strong>{{ day.label }}</strong><label><input type="checkbox" :value="day.key + '_lunch'" v-model="form.starter_custom_slots" /> Midi</label><label><input type="checkbox" :value="day.key + '_dinner'" v-model="form.starter_custom_slots" /> Soir</label></div></div></div>


        <div class="starter-divider"></div>
        <div class="pref-field">
          <label>En-cas dans mes menus</label>
          <p class="field-help">L’en-cas de l’après-midi est proposé par défaut. Je peux aussi ajouter un en-cas le matin.</p>
          <div class="starter-options snack-options">
            <label class="starter-option" :class="{ selected: form.snack_slots.includes('morning') }"><input type="checkbox" value="morning" v-model="form.snack_slots" /><span>Le matin</span></label>
            <label class="starter-option" :class="{ selected: form.snack_slots.includes('afternoon') }"><input type="checkbox" value="afternoon" v-model="form.snack_slots" /><span>L’après-midi</span></label>
          </div>
        </div>

        <div class="starter-divider"></div>
        <div class="pref-field"><label>Desserts dans mes menus</label><p class="field-help">Les desserts sont proposés uniquement avec les repas où les glucides sont déjà bien présents.</p>
          <div class="starter-options">
            <label v-for="choice in dessertChoices" :key="choice.value" class="starter-option" :class="{ selected: form.dessert_preference === choice.value }"><input type="radio" v-model="form.dessert_preference" :value="choice.value" /><span>{{ choice.label }}</span></label>
          </div>
        </div>

        <div class="starter-divider"></div>
        <div class="subsection-title">
          <strong>Petit déjeuner, viande et poisson</strong>
          <span>Ces préférences complètent les réglages de ma semaine.</span>
        </div>
        <Preferences :key="fineRevision" :diet-option="form.option" :initial-preferences="form" @draft-change="fineDraft = $event" @save-request="saveAllPreferences" />
      </div>
    </section>

    <!-- 2. ORGANISATION -->
    <section class="pref-section">
      <div class="section-heading clickable-heading" @click="organisationOpen = !organisationOpen">
        <span class="section-number">2</span>
        <div class="heading-copy"><h2>Comment j’organise mes repas</h2><p>Je précise les repas que je cuisine, ceux pris à l’extérieur ou à base de restes, ainsi que le temps dont je dispose.</p></div>
        <button type="button" class="open-section">{{ organisationOpen ? "Refermer" : "Voir / modifier" }}</button>
      </div>
      <div v-show="organisationOpen">
        <PreparationTime :busy="savingAll || regenerating" :save-feedback="saveFeedback" :save-error="saveError" @draft-change="organisationDraft = $event" @save-request="saveAllPreferences" @recalculate-request="applyPreferencesToMenus(false)" />

      </div>
    </section>

        <!-- 3. EXCLUSIONS -->
    <section class="pref-section">
      <div class="section-heading"><span class="section-number">3</span><div><h2>Ce que je ne peux / ne veux pas manger</h2><p>Ces exclusions s’appliquent à Mes menus de la semaine et à Mes repas personnalisés.</p></div></div>
    <!-- Exclusions d'aliments -->
    <div class="card" style="margin-top: 20px;">
      <div class="form-title">Ce que je ne peux / veux pas manger</div>

      <div v-if="nbExclusions >= nbExclusionsMax" style="color: red; font-weight: bold; margin-left: 10px;">
        Vous avez atteint la limite de {{ nbExclusionsMax }} aliments exclus maximum
      </div>

      <div class="excluor">
        <ExcluorButton v-model="form.exclude_eggs" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_eggs"
          @change="onExclusionChange">
          Œufs
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_arachid"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_arachid" @change="onExclusionChange">
          Arachides
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_cow_milk"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_cow_milk" @change="onExclusionChange">
          Lait de vache
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_nuts" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_nuts"
          @change="onExclusionChange">
          Fruits à coque
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_sea_food"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_sea_food" @change="onExclusionChange">
          Poissons et fruits de mer
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_soy" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_soy"
          @change="onExclusionChange">
          Soja
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_mustard"
          :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_mustard" @change="onExclusionChange">
          Moutarde
        </ExcluorButton>

        <ExcluorButton v-model="form.exclude_pork" :disabled="nbExclusions >= nbExclusionsMax && !form.exclude_pork"
          @change="onExclusionChange">
          Porc
        </ExcluorButton>

        <!-- ALIMENTS EXCLUS DYNAMIQUES -->
        <ExcluorButton v-for="aliment in form.excluded_aliments" :key="aliment.id" :value="true"
          @input="removeExcludedAliment(aliment)"
          :disabled="nbExclusions >= nbExclusionsMax && !isAlimentExcluded(aliment.id)" @change="onExclusionChange">
          {{ aliment.name }}
        </ExcluorButton>
      </div>

      <div style="margin: 10px;">
        <label class="form-label">Exclure d'autres aliments</label>
        <input class="form-input" placeholder="Rechercher un aliment en particulier..."
          @input="searchAliment($event.target.value)" />
        <p style="margin-top : 5px; margin-bottom : 0">
          Mes Meilleurs Menus ne sont pas adaptés pour une alimentation végétarienne sans œufs et/ou sans produits
          laitiers ou pour une alimentation végane.
        </p>
      </div>

      <div v-if="alimentsToExclude">
        <div style="display: flex; flex-wrap: wrap;">
          <div v-for="aliment in alimentsToExclude" :key="aliment.id" class="excluor-button"
            @click="excludeAliment(aliment)" :class="{ disabled: nbExclusions >= nbExclusionsMax }">
            {{ aliment.name }}
          </div>
        </div>
      </div>
    </div>

    </section>

<div class="pref-save-actions"><span v-if="saveFeedback" role="status">{{ saveFeedback }}</span><span v-if="saveError" class="pref-save-error" role="alert">{{ saveError }}</span><span v-if="deepSearchAvailable" class="deep-search-note">Le calcul rapide a atteint sa limite de temps.</span><button type="button" class="button" :disabled="savingAll || regenerating || restoringPrevious" @click="saveAllPreferences">{{ savingAll ? "Enregistrement…" : "Enregistrer sans recalculer" }}</button><button type="button" class="button" :disabled="savingAll || regenerating || restoringPrevious" @click="applyPreferencesToMenus(false)">{{ regenerating ? "Mise à jour des menus…" : "Valider mes préférences et mettre à jour ma semaine" }}</button><button v-if="canRestorePreferences" type="button" class="button button-secondary" :disabled="savingAll || regenerating || restoringPrevious" @click="restorePreviousPreferences">{{ restoringPrevious ? "Restauration…" : "Revenir à mes anciennes préférences" }}</button><button v-if="deepSearchAvailable" type="button" class="button deep-search-button" :disabled="regenerating" @click="applyPreferencesToMenus(true)">{{ deepSearchRunning ? "Recherche approfondie…" : "Lancer une recherche approfondie (jusqu’à 2 min)" }}</button></div>

    <!-- PROFILS DU FOYER -->
    <section class="pref-section profile-section">
      <div class="section-heading">
        <span class="section-number">4</span>
        <div>
          <h2>Mon profil et mon foyer</h2>
          <p>Âge, sexe, taille, poids, activité physique et objectif de poids servent à personnaliser aussi bien Mes menus de la semaine que Mes repas personnalisés.</p>
          <p class="protein-profile-note">
            Pour les sportifs et les personnes âgées, MMM adapte automatiquement l’objectif protéique à leurs besoins physiologiques.
          </p>
        </div>
      </div>
    </section>
    <div class="row" style="margin-top: 20px;">
      <div class="col-md-2" style="flex: 1">
        <ConsumerProfil v-for="(consumer, index) in consumers" :key="consumer.id" :value="consumer"
          :firstPerson="index === 0" @deleted="removeConsumerFromList" @edit="openEditConsumerModal"
          style="margin-bottom: 10px" />
      </div>

      <div class="col-md-2" style="flex: 1; display: flex; align-items: center; justify-content: center;">
        <button class="button" @click="openAddConsumerModal">
          Ajouter quelqu'un d'autre dans mon foyer
        </button>
      </div>
    </div>

    <!-- MODAL AJOUT / ÉDITION -->
    <EditProfilModal v-if="showModal" :profil="currentConsumer" :firstPerson="currentConsumerFirstPerson" @close="closeModal"
      @update="saveConsumer" />
  </div>
</template>

<script>
import { userState } from "@/utils/store";
import ExcluorButton from "@/components/ExcluorButton.vue";
import ConsumerProfil from "@/components/ConsumerProfil.vue";
import Preferences from "@/components/Preferences.vue";
import PreparationTime from "@/components/PreparationTime.vue";
import EditProfilModal from "@/components/EditProfilModal.vue";
import { api } from "@/api";

export default {
  name: "MesPreferences",
  props: { embedded: { type: Boolean, default: false } },
  components: {
    ExcluorButton,
    ConsumerProfil,
    Preferences,
    PreparationTime,
    EditProfilModal,
  },

  data() {
    return {
      form: null,
      batchChoice:false, batchWindowOpen:false, batchChoiceSaving:false, batchChoiceError:"",
      fineRevision: 0, fineDraft: null, organisationDraft: null, savedPreferenceSnapshot: null,
      savingAll: false, regenerating: false, openingMealPrep: false, restoringPrevious: false, previousPreferencesAvailable: false, deepSearchAvailable: false, deepSearchRunning: false, saveFeedback: "", saveError: "", showLeaveDialog: false, pendingLeave: null,
      showMealPrepDisableConfirm: false, savingMealPrepOption: false,
      aliments: null,
      nbExclusionsMax: 20,

      showModal: false,
      currentConsumer: null,
      currentConsumerFirstPerson: false,
      savingStarter: false,
      starterSaved: false,
      savingMain: false,
      mainSaved: false,
      savingBatch: false,
      batchSaved: false,
      organisationOpen: false,
      batchDays: [
        { value: "monday", label: "Lundi" },
        { value: "tuesday", label: "Mardi" },
        { value: "wednesday", label: "Mercredi" },
        { value: "thursday", label: "Jeudi" },
        { value: "friday", label: "Vendredi" },
        { value: "saturday", label: "Samedi" },
        { value: "sunday", label: "Dimanche" },
      ],
      starterChoices: [
        { value: "none", label: "Non, je préfère mes menus sans entrée" },
        { value: "lunch", label: "Oui, au déjeuner" },
        { value: "dinner", label: "Oui, au dîner" },
        { value: "both", label: "Oui, au déjeuner et au dîner" },
        { value: "custom", label: "Oui, mais je choisis quand je veux une entrée" },
      ],
      dessertChoices: [
        { value: "none", label: "Non, sans dessert" },
        { value: "lunch", label: "Oui, au déjeuner" },
        { value: "dinner", label: "Oui, au dîner" },
        { value: "both", label: "Oui, au déjeuner et au dîner" },
      ],
      starterDays: [
        { key: "monday", label: "Lundi" },
        { key: "tuesday", label: "Mardi" },
        { key: "wednesday", label: "Mercredi" },
        { key: "thursday", label: "Jeudi" },
        { key: "friday", label: "Vendredi" },
        { key: "saturday", label: "Samedi" },
        { key: "sunday", label: "Dimanche" },
      ],
    };
  },

  computed: {
    hasUnsavedChanges() { return this.savedPreferenceSnapshot !== null && JSON.stringify(this.preferencePayload()) !== this.savedPreferenceSnapshot; },
    canRestorePreferences() { return this.previousPreferencesAvailable || this.hasUnsavedChanges; },
    preferences() {
      return userState.me.consumer_account;
    },

    consumers() {
      return this.preferences.consumers;
    },

    alimentsToExclude() {
      if (!this.form || !this.aliments) return [];
      return this.aliments.filter(
        (aliment) =>
          !this.form.excluded_aliments.find((a) => a.id === aliment.id)
      );
    },

    batchSlotChoices() {
      return (this.form?.batch_cooking_slots || []).map(slot => {
        const day = this.batchDays.find(item => item.value === slot.day);
        const period = {
          morning: "matin",
          afternoon: "après-midi",
          evening: "soir",
        }[slot.period] || slot.period;
        return {
          value: `${slot.day}_${slot.period}`,
          label: `${day?.label || slot.day} ${period}`,
        };
      });
    },

    nbExclusions() {
      if (!this.form) return 0;
      return (
        this.form.excluded_aliments.length +
        (this.form.exclude_eggs ? 1 : 0) +
        (this.form.exclude_arachid ? 1 : 0) +
        (this.form.exclude_cow_milk ? 1 : 0) +
        (this.form.exclude_nuts ? 1 : 0) +
        (this.form.exclude_sea_food ? 1 : 0) +
        (this.form.exclude_soy ? 1 : 0) +
        (this.form.exclude_mustard ? 1 : 0) +
        (this.form.exclude_pork ? 1 : 0)
      );
    },
  },

  mounted() {
    this.reset();
    this.batchChoice = !!this.preferences.batch_cooking_enabled;
    try { this.previousPreferencesAvailable = !!localStorage.getItem(this.previousPreferencesKey()); } catch (_) { /* Storage unavailable. */ }
    this.$nextTick(() => { this.savedPreferenceSnapshot = JSON.stringify(this.preferencePayload()); });
    window.addEventListener("beforeunload", this.warnBeforeUnload);
  },

  beforeDestroy() { window.removeEventListener("beforeunload", this.warnBeforeUnload); if (this.pendingLeave) this.pendingLeave(false); },
  beforeRouteLeave(to, from, next) { this.requestLeave(next); },
  methods: {
    requestLeave(next) {
      this.batchWindowOpen = false;
    if (this.regenerating) { this.saveFeedback="La mise à jour est en cours. Merci de patienter avant de quitter."; next(false); return; }
    if (!this.hasUnsavedChanges) { next(); return; }
    this.pendingLeave = next; this.showLeaveDialog = true;
    },

    async confirmBatchChoice() {
      this.batchChoiceError="";
      if(this.batchChoice) { this.batchWindowOpen=true; return; }
      this.batchChoiceSaving=true;
      try { await userState.savePreferences({batch_cooking_enabled:false,batch_cooking_slots:[],batch_cooking_meals:[]},{regenerate:false,timeout:15000}); this.batchWindowOpen=false; }
      catch(error) { this.batchChoiceError="Le réglage n’a pas pu être enregistré. Réessayez."; }
      finally { this.batchChoiceSaving=false; }
    },
    preferencePayload() {
      if (!this.form) return {};
      const body = JSON.parse(JSON.stringify({ ...this.form, ...(this.fineDraft || {}), ...(this.organisationDraft || {}) }));
      // Keep saved meat/fish choices when temporarily selecting vegetarian; the backend filters that diet.
      if (body.starter_preference !== "custom") body.starter_custom_slots=[];
      if (body.dessert_preference !== "custom") body.dessert_custom_slots=[];
      body.snack_slots = Array.isArray(body.snack_slots) ? body.snack_slots.filter(slot => ["morning", "afternoon"].includes(slot)) : ["afternoon"];
      body.batch_cooking_enabled = false;
      body.batch_cooking_slots = [];
      body.batch_cooking_meals = [];
      return body;
    },
    async openMealPrepFromPreferences() {
      if (this.openingMealPrep || this.savingAll || this.regenerating) return;
      this.openingMealPrep = true;
      const saved = await this.saveAllPreferences();
      this.openingMealPrep = false;
      if (saved) this.$emit("meal-prep-request");
    },
    pendingPreferencesKey() {
      const me = userState.me || {};
      const home = me.consumer_account || {};
      return 'mmm-week-preferences-pending:' + (home.id || me.id || 'anonymous');
    },
    previousPreferencesKey() { return this.pendingPreferencesKey() + ':previous'; },
    async saveAllPreferences() {
      if (this.savingAll || this.regenerating) return false;
      this.savingAll=true; this.saveError=""; this.saveFeedback="";
      try {
        await this.$nextTick();
        const body=this.preferencePayload();
        const snapshot=JSON.stringify(body);
        let weeklyChanged = false;
        if (this.savedPreferenceSnapshot) {
          const previous = JSON.parse(this.savedPreferenceSnapshot);
          const current = { ...body };
          ['meal_prep_grouping_enabled', 'meal_prep_group_size', 'batch_cooking_enabled', 'batch_cooking_slots', 'batch_cooking_meals'].forEach(key => {
            delete previous[key];
            delete current[key];
          });
          weeklyChanged = JSON.stringify(previous) !== JSON.stringify(current);
        }
        await userState.savePreferences(body,{regenerate:false,timeout:15000});
        if (weeklyChanged) {
          if (!this.restoringPrevious && this.savedPreferenceSnapshot) {
            try {
              localStorage.setItem(this.previousPreferencesKey(), this.savedPreferenceSnapshot);
              this.previousPreferencesAvailable = true;
            } catch (_) { /* Storage unavailable. */ }
          }
          try { localStorage.setItem(this.pendingPreferencesKey(), '1'); } catch (_) { /* Storage unavailable. */ }
          this.saveFeedback = 'Enregistré ✓ Pour appliquer ces choix à cette semaine, cliquez sur « Mettre à jour ma semaine de menus ».';
        }
        const account = userState.me?.consumer_account;
        if (account) {
          account.meal_prep_grouping_enabled = !!body.meal_prep_grouping_enabled;
          account.meal_prep_group_size = Number(body.meal_prep_group_size) || 3;
        }
        this.savedPreferenceSnapshot=snapshot;
        if (!weeklyChanged) this.saveFeedback="Enregistré ✓";
        return true;
      } catch(error) {
        const data=error.response?.data;
        const messages=data && typeof data==="object"
          ? Object.values(data).flat().filter(value=>typeof value==="string").join(" ")
          : "";
        this.saveError=messages || (error.code==="ECONNABORTED"
          ? "Le serveur met trop de temps à répondre. Vos choix sont conservés sur cette page ; réessayez."
          : "L’enregistrement a échoué. Vos choix sont conservés sur cette page ; réessayez.");
        return false;
      }
      finally { this.savingAll=false; }
    },
    onMealPrepOptionChange(enabled) {
      if (enabled) {
        this.form.meal_prep_grouping_enabled = true;
        return;
      }
      if (this.preferences?.meal_prep_grouping_enabled) {
        this.form.meal_prep_grouping_enabled = true;
        this.showMealPrepDisableConfirm = true;
        return;
      }
      this.form.meal_prep_grouping_enabled = false;
    },
    async confirmMealPrepDisable() {
      if (this.savingMealPrepOption) return;
      this.savingMealPrepOption = true;
      this.saveError = "";
      try {
        await userState.savePreferences({ meal_prep_grouping_enabled: false }, { regenerate: false, timeout: 15000 });
        this.form.meal_prep_grouping_enabled = false;
        const account = userState.me?.consumer_account;
        if (account) account.meal_prep_grouping_enabled = false;
        if (this.savedPreferenceSnapshot) {
          const saved = JSON.parse(this.savedPreferenceSnapshot);
          saved.meal_prep_grouping_enabled = false;
          this.savedPreferenceSnapshot = JSON.stringify(saved);
        }
        this.showMealPrepDisableConfirm = false;
        this.saveFeedback = "Option retirée. Votre semaine reste inchangée.";
      } catch (error) {
        this.saveError = "L’option n’a pas pu être retirée. Réessayez.";
      } finally {
        this.savingMealPrepOption = false;
      }
    },
    mealPrepSettingsOnlyChanged() {
      if (!this.savedPreferenceSnapshot) return false;
      try {
        const previous = JSON.parse(this.savedPreferenceSnapshot);
        const current = this.preferencePayload();
        delete previous.meal_prep_grouping_enabled;
        delete previous.meal_prep_group_size;
        delete current.meal_prep_grouping_enabled;
        delete current.meal_prep_group_size;
        return JSON.stringify(previous) === JSON.stringify(current);
      } catch (error) {
        return false;
      }
    },
    async restorePreviousPreferences() {
      if (this.savingAll || this.regenerating || this.restoringPrevious) return;
      const previous = (() => {
        try { return JSON.parse(localStorage.getItem(this.previousPreferencesKey()) || "null"); }
        catch (_) { return null; }
      })();
      if (!previous) {
        this.reset();
        this.fineDraft = null;
        this.organisationDraft = null;
        this.savedPreferenceSnapshot = JSON.stringify(this.preferencePayload());
        this.saveFeedback = "Vos préférences enregistrées ont été restaurées dans le formulaire.";
        return;
      }
      this.restoringPrevious = true;
      this.saveError = "";
      try {
        this.form = { ...this.form, ...previous };
        this.fineRevision += 1;
        this.fineDraft = null;
        this.organisationDraft = null;
        if (!await this.saveAllPreferences()) return;
        this.regenerating = true;
        this.saveFeedback = "Je rétablis vos anciennes préférences et je mets à jour la semaine… Cela prend maintenant environ 40 secondes au maximum.";
        const response = await api.post("/regenerate-my-menus/", {}, { timeout: 600000 });
        if (response.data?.success !== true) throw new Error("La mise à jour n’a pas été confirmée.");
        userState.menuRevision += 1;
        try {
          localStorage.removeItem(this.pendingPreferencesKey());
          localStorage.removeItem(this.previousPreferencesKey());
        } catch (_) { /* Storage unavailable. */ }
        this.previousPreferencesAvailable = false;
        this.saveFeedback = "Vos anciennes préférences sont rétablies et la semaine a été mise à jour.";
        if (this.embedded) this.$emit("menus-updated");
        else await this.$router.push("/mes-menus");
      } catch (error) {
        this.saveError = error.response?.data?.detail || "Les préférences ont été restaurées, mais la semaine n’a pas pu être recalculée. Réessayez.";
      } finally {
        this.regenerating = false;
        this.restoringPrevious = false;
      }
    },
    async applyPreferencesToMenus(deepSearch = false) {
      deepSearch = deepSearch === true;
      if(this.regenerating || this.savingAll) return;
      if(!await this.saveAllPreferences()) return;
      this.regenerating=true;
      this.deepSearchRunning=deepSearch;
      this.deepSearchAvailable=false;
      this.saveError="";
      this.saveFeedback=deepSearch
        ? "Je lance une recherche approfondie avec vos préférences. Cela peut prendre jusqu’à 2 minutes."
        : "Je mets à jour la semaine avec vos préférences. Le calcul rapide prend quelques dizaines de secondes.";
      try {
        const response=await api.post("/regenerate-my-menus/", { deep_search: deepSearch }, {timeout:600000});
        if(response.data?.success !== true) throw new Error("La mise à jour n’a pas été confirmée.");
        userState.menuRevision += 1;
        try { localStorage.removeItem(this.pendingPreferencesKey()); } catch (_) { /* Storage unavailable. */ }
        this.saveFeedback="Préférences appliquées : mes menus sont à jour.";
        this.regenerating=false;
        if (this.embedded) this.$emit("menus-updated");
        else await this.$router.push("/mes-menus");
      } catch(error) {
        this.saveFeedback="";
        this.deepSearchAvailable=!!error.response?.data?.deep_search_available;
        this.saveError=error.response?.data?.detail || "Vos préférences sont enregistrées, mais la mise à jour des menus n’a pas été confirmée. Réessayez.";
      } finally { this.regenerating=false; this.deepSearchRunning=false; }
    },
    warnBeforeUnload(event) { if(!this.hasUnsavedChanges&&!this.savingAll&&!this.regenerating) return; event.preventDefault(); event.returnValue=""; },
    async resolveLeave(save) { if(save&&!await this.saveAllPreferences()) return; const next=this.pendingLeave; this.pendingLeave=null; this.showLeaveDialog=false; if(next) next(); },
    cancelLeave() { const next=this.pendingLeave; this.pendingLeave=null; this.showLeaveDialog=false; if(next) next(false); },
    reset() {
      this.fineDraft = null;
      this.fineRevision += 1;
      this.form = {
        nb_meat_dish_per_week: this.preferences.nb_meat_dish_per_week ?? 4,
        nb_fish_dish_per_week: this.preferences.nb_fish_dish_per_week ?? 3,
        prefered_breakfast: this.preferences.prefered_breakfast,
        same_breakfast_all_week: this.preferences.same_breakfast_all_week,
        breakfast_week_preferences: this.preferences.breakfast_week_preferences,
        preferences_for_when_to_eat_meat_and_fish: this.preferences.preferences_for_when_to_eat_meat_and_fish,
        excluded_aliments: (this.preferences.excluded_aliments || []).map(aliment => ({ ...aliment })),
        exclude_eggs: this.preferences.exclude_eggs,
        exclude_arachid: this.preferences.exclude_arachid,
        exclude_cow_milk: this.preferences.exclude_cow_milk,
        exclude_nuts: this.preferences.exclude_nuts,
        exclude_sea_food: this.preferences.exclude_sea_food,
        exclude_soy: this.preferences.exclude_soy,
        exclude_mustard: this.preferences.exclude_mustard,
        exclude_pork: this.preferences.exclude_pork,
        option: this.preferences.option === "low-glycemic-index" ? "low-glycemic" : (this.preferences.option || "none"),
        prefer_cheap_meals: !!this.preferences.prefer_cheap_meals,
        starter_preference: this.preferences.starter_preference || "lunch",
        starter_custom_slots: [...(this.preferences.starter_custom_slots || [])],
        snack_slots: Array.isArray(this.preferences.snack_slots) ? [...this.preferences.snack_slots] : ["afternoon"],
        dessert_preference: this.preferences.dessert_preference === "custom" ? "both" : (this.preferences.dessert_preference || "both"),
        dessert_custom_slots: [],
        protein_mode: this.preferences.protein_mode || "standard",
        meal_prep_grouping_enabled: !!this.preferences.meal_prep_grouping_enabled,
        meal_prep_group_size: [2, 3, 4, 5].includes(Number(this.preferences.meal_prep_group_size)) ? Number(this.preferences.meal_prep_group_size) : 3,
        batch_cooking_enabled: !!this.preferences.batch_cooking_enabled,
        batch_cooking_slots:
          Array.isArray(this.preferences.batch_cooking_slots) &&
          this.preferences.batch_cooking_slots.length
            ? this.preferences.batch_cooking_slots.map(slot => ({ ...slot }))
            : [{ day: "sunday", period: "afternoon", duration: 90 }],
        batch_cooking_meals:
          Array.isArray(this.preferences.batch_cooking_meals) &&
          this.preferences.batch_cooking_meals.length
            ? this.preferences.batch_cooking_meals.map(meal => ({
                ...meal,
                consumer_ids: Array.isArray(meal.consumer_ids)
                  ? [...meal.consumer_ids]
                  : [],
              }))
            : [],
      };
    },

    async saveMainPreferences() {
      this.savingMain = true;
      this.mainSaved = false;
      try {
        await userState.savePreferences({
          option: this.form.option,
          prefer_cheap_meals: this.form.prefer_cheap_meals,
          protein_mode: this.form.protein_mode,
        }, { regenerate: false });
        try { localStorage.setItem(this.pendingPreferencesKey(), "1"); } catch (_) { /* Storage unavailable. */ }
        this.mainSaved = true;
        window.setTimeout(() => { this.mainSaved = false; }, 2500);
      } finally { this.savingMain = false; }
    },

    async saveStarterPreference() {
      this.savingStarter = true;
      this.starterSaved = false;
      try {
        const slots = this.form.starter_preference === "custom"
          ? this.form.starter_custom_slots
          : [];
        await userState.savePreferences({
          starter_preference: this.form.starter_preference,
          starter_custom_slots: slots,
        }, { regenerate: false });

        try { localStorage.setItem(this.pendingPreferencesKey(), "1"); } catch (_) { /* Storage unavailable. */ }
        this.form.starter_custom_slots = [...slots];
        this.starterSaved = true;
        window.setTimeout(() => {
          this.starterSaved = false;
        }, 2500);
      } finally {
        this.savingStarter = false;
      }
    },

    addBatchSlot() {
      this.form.batch_cooking_slots.push({
        day: "sunday",
        period: "afternoon",
        duration: 90,
      });
    },

    removeBatchSlot(index) {
      this.form.batch_cooking_slots.splice(index, 1);
      if (!this.form.batch_cooking_slots.length) {
        this.addBatchSlot();
      }
    },

    addBatchMeal() {
      const firstSlot = this.batchSlotChoices[0]?.value || "sunday_afternoon";
      this.form.batch_cooking_meals.push({
        day: "monday",
        meal: "lunch",
        prep_key: firstSlot,
        consumer_ids: this.consumers.map(consumer => consumer.id),
      });
    },

    removeBatchMeal(index) {
      this.form.batch_cooking_meals.splice(index, 1);
    },

    async saveBatchCooking() {
      this.savingBatch = true;
      this.batchSaved = false;
      try {
        const slots = this.form.batch_cooking_enabled
          ? this.form.batch_cooking_slots.map(slot => ({
              day: slot.day,
              period: slot.period,
              duration: Number(slot.duration) || 60,
            }))
          : [];

        const validPrepKeys = new Set(
          slots.map(slot => `${slot.day}_${slot.period}`)
        );

        const meals = this.form.batch_cooking_enabled
          ? this.form.batch_cooking_meals
              .map(meal => ({
                day: meal.day,
                meal: meal.meal,
                prep_key: validPrepKeys.has(meal.prep_key)
                  ? meal.prep_key
                  : (slots[0] ? `${slots[0].day}_${slots[0].period}` : ""),
                consumer_ids: (meal.consumer_ids || [])
                  .map(Number)
                  .filter(Boolean),
              }))
              .filter(meal => meal.prep_key && meal.consumer_ids.length)
          : [];

        await userState.savePreferences({
          batch_cooking_enabled: this.form.batch_cooking_enabled,
          batch_cooking_slots: slots,
          batch_cooking_meals: meals,
        }, { regenerate: false });

        this.form.batch_cooking_slots = slots.length
          ? slots.map(slot => ({ ...slot }))
          : [{ day: "sunday", period: "afternoon", duration: 90 }];
        this.form.batch_cooking_meals = meals.map(meal => ({
          ...meal,
          consumer_ids: [...meal.consumer_ids],
        }));

        this.batchSaved = true;
        window.setTimeout(() => {
          this.batchSaved = false;
        }, 2500);
      } finally {
        this.savingBatch = false;
      }
    },

    searchAliment(q) {
      if (q.length < 3) {
        this.aliments = [];
        return;
      }
      api.get(`/aliments?q=${q}`).then((response) => {
        this.aliments = response.data;
      });
    },

    onExclusionChange() { this.saveFeedback = ""; },

    excludeAliment(aliment) {
      this.form.excluded_aliments.push(aliment);
      this.onExclusionChange();
    },

    removeExcludedAliment(aliment) {
      this.form.excluded_aliments = this.form.excluded_aliments.filter(
        (a) => a.id !== aliment.id
      );
      this.onExclusionChange();
    },

    isAlimentExcluded(id) {
      return this.form.excluded_aliments.find((a) => a.id === id);
    },

    /* ----------------------------- FOYER ----------------------------- */

    openAddConsumerModal() {
      this.currentConsumerFirstPerson = false;
      this.currentConsumer = {
        name: "",
        year_of_birth: "",
        gender: "male",
        want_to_loose_weight: false,
        height: "",
        weight: "",
        high_intensity_activity: 0,
        medium_intensity_activity: 0,
        low_intensity_activity: 0,
        sport_adaptation_enabled: false,
        daily_activity_level: "low",
      };

      this.showModal = true;
    },

    openEditConsumerModal(consumer) {
      const index = this.preferences.consumers.findIndex((c) => c.id === consumer.id);
      this.currentConsumerFirstPerson = index === 0;
      this.currentConsumer = { ...consumer };
      this.showModal = true;
    },

    saveConsumer(updatedConsumer) {
      // Si ID existe → update
      if (updatedConsumer.id) {
        const index = this.preferences.consumers.findIndex(
          (c) => c.id === updatedConsumer.id
        );
        this.preferences.consumers.splice(index, 1, updatedConsumer);
      } else {
        // Sinon → nouveau foyer
        this.preferences.consumers.push(updatedConsumer);
      }

      this.showModal = false;
    },

    closeModal() {
      this.showModal = false;
    },

    removeConsumerFromList(id) {
      this.preferences.consumers = this.preferences.consumers.filter(
        (c) => c.id !== id
      );
    },
  },
};
</script>

<style scoped>
.pref-section { margin-bottom: 28px; }
.section-heading { display:flex; align-items:flex-start; gap:12px; margin-bottom:12px; }
.section-heading h2 { margin:0; color:#466b22; font-size:24px; }
.section-heading p { margin:4px 0 0; color:#5f6658; }
.clickable-heading { cursor:pointer; background:#f7f9f3; border:1px solid #e0e7d7; border-radius:14px; padding:16px 18px; align-items:center; }
.heading-copy { flex:1; }
.open-section { border:0; background:#6f9634; color:#fff; border-radius:9px; padding:10px 15px; font-weight:700; cursor:pointer; }
.section-number { width:34px; height:34px; flex:0 0 34px; border-radius:50%; background:#6f9634; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:18px; }
.main-pref-card { padding:22px; }
.pref-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:18px; align-items:start; }
.pref-field > label { display:block; font-weight:700; margin-bottom:7px; }
.field-help { margin:0 0 10px; color:#5f6658; font-size:14px; }
.diet-select {
  font-family: "TangoSans", Arial, sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
}
.diet-select option {
  font-family: "TangoSans", Arial, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
  padding: 8px 10px;
}
.binary-options { display:flex; gap:8px; }
.binary-options label { min-width:80px; padding:9px 12px; border:2px solid rgba(60,102,16,.18); border-radius:9px; cursor:pointer; text-align:center; }
.binary-options label.selected { border-color:#6f9634; background:#f4f8ed; }
.protein-options { flex-wrap: wrap; }
.protein-options label { min-width: 145px; }
.pref-save { display:flex; gap:10px; align-items:center; justify-content:flex-end; margin-top:18px; }
.protein-pref-row { margin-top:20px; padding-top:18px; border-top:1px solid #e1e8d8; }
.label-with-help { display:flex !important; align-items:center; gap:7px; }
.pref-help {
  position:relative;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:20px;
  height:20px;
  border:1px solid #6f9634;
  border-radius:50%;
  color:#466b22;
  background:#f4f8ed;
  font-size:12px;
  font-weight:800;
  cursor:help;
  flex:0 0 20px;
  outline:none;
}
.pref-help:hover::after,
.pref-help:focus::after {
  content:attr(data-help);
  position:absolute;
  left:50%;
  bottom:calc(100% + 9px);
  transform:translateX(-50%);
  z-index:80;
  width:min(320px,75vw);
  padding:10px 12px;
  border-radius:9px;
  background:#26351b;
  color:#fff;
  font-size:12px;
  font-weight:500;
  line-height:1.4;
  text-align:left;
  box-shadow:0 6px 18px rgba(0,0,0,.18);
}
.pref-help:hover::before,
.pref-help:focus::before {
  content:"";
  position:absolute;
  left:50%;
  bottom:calc(100% + 3px);
  transform:translateX(-50%);
  border:6px solid transparent;
  border-top-color:#26351b;
  z-index:81;
}
.saved-msg { font-weight:700; color:#3c6610; }
.starter-divider { border-top:1px solid #e1e8d8; margin:22px 0 18px; }
.subsection-title { display:flex; flex-direction:column; gap:3px; margin-bottom:12px; }
.subsection-title strong { color:#466b22; font-size:18px; }
.subsection-title span { color:#5f6658; font-size:14px; }
.batch-placeholder { margin-top:12px; padding:18px 22px; display:flex; justify-content:space-between; gap:20px; align-items:center; border-style:dashed; }
.batch-placeholder p { margin:5px 0 0; color:#5f6658; }
.coming-soon { white-space:nowrap; background:#eef4e6; color:#52772a; border-radius:999px; padding:7px 11px; font-weight:700; }
@media (max-width:900px) { .pref-grid { grid-template-columns:1fr; } .pref-save { justify-content:flex-start; } }

.starter-card {
  padding: 22px;
}
.starter-intro {
  margin: 4px 0 18px;
  max-width: 850px;
}
.starter-options {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 10px;
}
.starter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 58px;
  padding: 10px 12px;
  border: 2px solid rgba(60, 102, 16, 0.18);
  border-radius: 10px;
  background: rgba(255,255,255,0.45);
  cursor: pointer;
  font-weight: 600;
}
.starter-option.selected {
  border-color: #6f9634;
  background: rgba(255,255,255,0.78);
}
.starter-custom {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(60, 102, 16, 0.22);
}
.starter-custom-title {
  font-weight: 700;
  margin-bottom: 10px;
}
.starter-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
}
.starter-day {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px;
  border-radius: 9px;
  background: rgba(255,255,255,0.5);
}
.starter-day label {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}
.starter-actions {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}
.starter-saved {
  font-weight: 700;
  color: #3c6610;
}
@media (max-width: 1100px) {
  .starter-options { grid-template-columns: repeat(2, 1fr); }
  .starter-week { grid-template-columns: repeat(4, 1fr); }
}
@media (max-width: 650px) {
  .starter-options, .starter-week { grid-template-columns: 1fr; }
}

.aliment-to-exclude {
  padding: 10px;
  margin: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  cursor: pointer;
}

.diet-select,
.diet-select option {
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #263322;
}

.batch-card {
  margin-top: 16px;
  padding: 22px;
  border: 1px solid #d8e8c5;
  border-radius: 16px;
  background: #f8fbf3;
}
.batch-heading {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
}
.batch-heading > div:first-child {
  flex: 1;
}
.batch-heading strong {
  color: #315b19;
  font-size: 18px;
}
.batch-heading p {
  margin: 5px 0 0;
  color: #5f6658;
}
.batch-body {
  margin-top: 18px;
}
.batch-note {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-left: 4px solid #8aad47;
  background: #fff;
  color: #5f6658;
  font-size: 13px;
  line-height: 1.4;
}
.batch-table {
  border: 1px solid #dce5d4;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.batch-table-head,
.batch-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1.1fr 42px;
  gap: 9px;
  align-items: center;
}
.batch-table-head {
  padding: 10px 12px;
  background: #edf5e3;
  color: #315b19;
}
.batch-row {
  padding: 10px 12px;
  border-top: 1px solid #edf1e9;
}
.batch-row select {
  width: 100%;
  padding: 9px 10px;
  border: 1px solid #d3ddca;
  border-radius: 8px;
  background: #fff;
}
.batch-remove {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #fff2ef;
  color: #a33c31;
  font-size: 20px;
  cursor: pointer;
}
.batch-add {
  margin-top: 10px;
  border: 1px solid #b8caa7;
  border-radius: 9px;
  padding: 9px 13px;
  background: #fff;
  color: #466b22;
  font-weight: 800;
  cursor: pointer;
}
.batch-subtitle {
  margin: 18px 0 10px;
  color: #315b19;
  font-size: 16px;
  font-weight: 800;
}
.batch-subtitle-spaced {
  margin-top: 24px;
}
.batch-meals {
  display: grid;
  gap: 10px;
}
.batch-meal-card {
  padding: 12px;
  border: 1px solid #dce5d4;
  border-radius: 12px;
  background: #fff;
}
.batch-meal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1.35fr 42px;
  gap: 9px;
  align-items: end;
}
.batch-meal-grid label {
  color: #5f6658;
  font-size: 12px;
  font-weight: 700;
}
.batch-meal-grid select {
  width: 100%;
  margin-top: 5px;
  padding: 9px 10px;
  border: 1px solid #d3ddca;
  border-radius: 8px;
  background: #fff;
}
.batch-people {
  display: flex;
  flex-wrap: wrap;
  gap: 9px 14px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #edf1e9;
}
.batch-people strong {
  width: 100%;
  color: #315b19;
}
.batch-people label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4f5c49;
  font-weight: 700;
}
.batch-meal-remove {
  align-self: end;
}
.batch-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
@media (max-width: 760px) {
  .batch-heading {
    flex-direction: column;
  }
  .batch-table-head {
    display: none;
  }
  .batch-row,
  .batch-meal-grid {
    grid-template-columns: 1fr;
  }
  .batch-remove {
    justify-self: end;
  }
  .batch-actions {
    align-items: stretch;
    flex-direction: column;
  }
}
.batch-simple-choice{display:flex;gap:24px;align-items:center;padding:18px;background:#f4f7ef;border-radius:10px}.batch-simple-choice label{display:flex;gap:8px;align-items:center;font-weight:700}.batch-modal-backdrop{position:fixed;inset:0;background:rgba(20,35,12,.55);z-index:1500;display:flex;align-items:center;justify-content:center;padding:20px}.batch-modal{background:white;border-radius:16px;width:min(1150px,100%);max-height:90vh;overflow-y:auto;overscroll-behavior:contain}.batch-modal>header{position:sticky;top:0;z-index:2;display:flex;justify-content:space-between;gap:15px;align-items:center;padding:18px 24px;background:#f4f7ef;border-bottom:1px solid #dce5d3}.batch-modal header h2{margin:0;font-size:1.4rem}.batch-modal header p{margin:7px 0 0;font-size:.9rem}.batch-modal-close{padding:10px 14px;border:1px solid #436f0e;background:white;border-radius:8px;font-weight:700;color:#436f0e}@media(max-width:650px){.batch-modal-backdrop{padding:8px}.batch-modal{max-height:95vh}.batch-modal>header{padding:12px}.batch-modal header p{display:none}}
</style>

<style scoped>
 .meal-prep-toggle{display:flex!important;align-items:center;gap:12px;width:100%;padding:14px 16px;margin-bottom:10px;background:#f4f8ed;border:1px solid #d9e4ca;border-radius:12px;font-weight:700;color:#2f4827;font-size:inherit;text-align:left;cursor:pointer}.meal-prep-toggle input{flex:0 0 auto;margin:0 2px 0 0}.meal-prep-toggle span{display:block}.meal-prep-grouping-controls>p{grid-column:1/-1}.meal-prep-toggle.selected{border-color:#6f9634;background:#f4f8ed}.meal-prep-grouping-controls{display:grid;grid-template-columns:minmax(180px,1fr) minmax(130px,180px);gap:8px 12px;align-items:center}.meal-prep-grouping-controls>label{font-weight:700}.meal-prep-grouping-controls select{width:100%}.meal-prep-recalculate-button{grid-column:1/-1;justify-self:start;margin-top:6px}.meal-prep-confirm-overlay{z-index:2100}
.meal-prep-options{grid-template-columns:repeat(2,minmax(280px,560px))}
.meal-prep-options .starter-option{min-height:64px}
.meal-prep-grouping-controls{margin-top:12px}
.meal-prep-group-size-row{display:flex;align-items:center;justify-content:flex-start;gap:16px;flex-wrap:wrap}
.meal-prep-group-size-row label{font-weight:700}
.meal-prep-group-size-row select{width:165px;max-width:100%}
.meal-prep-open-button{grid-column:1/-1;justify-self:start;margin-top:4px}
@media(max-width:600px){.meal-prep-options{grid-template-columns:1fr}.meal-prep-group-size-row{align-items:flex-start;gap:8px}.meal-prep-group-size-row label{width:100%}}
@media (max-width: 600px){.meal-prep-grouping-controls{grid-template-columns:1fr}.meal-prep-recalculate-button{grid-column:1}}
.pref-save-actions{display:flex;justify-content:flex-end;align-items:center;gap:12px;flex-wrap:wrap;margin:12px 0 24px}.pref-save-actions button{flex-shrink:0}.pref-save-actions>span{font-size:13px;color:#436f0e}.pref-save-actions .pref-save-error{color:#a43424}.leave-overlay{position:fixed;inset:0;z-index:2000;background:#0007;display:flex;align-items:center;justify-content:center;padding:20px}.leave-dialog{background:white;padding:24px;max-width:540px;border-radius:14px}.leave-dialog button{margin:6px}
.pantry-preference{display:flex;justify-content:space-between;align-items:center;gap:16px;margin:16px 0;padding:14px 16px;border:1px solid #dce6d0;border-radius:12px;background:#f7faf2}.pantry-preference strong{color:#315b19}.pantry-preference p{margin:4px 0 0;color:#65705f;font-size:13px}@media(max-width:600px){.pantry-preference{align-items:stretch;flex-direction:column}.pantry-preference .button{align-self:flex-start}}
</style>
